import { useEffect, useRef, useState } from 'react';


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/21/2025
 */
export default function useDebouncedValue<T>(initialValue: T, delay: number) {

    const [debouncedValue, setDebouncedValue] = useState<T>(initialValue); // Value to emit
    const lastValue = useRef<T>(initialValue); // Ref to hold the last assigned value
    const timeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref to manage the debounce timer

    const setValue = (value: T) => {
        // Update the last value
        lastValue.current = value;

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set a new timeout to emit the last value after the specified delay
        timeoutRef.current = setTimeout(() => {
            setDebouncedValue(lastValue.current);
        }, delay);
    };

    useEffect(() => {
        // Cleanup the timeout on component unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return [debouncedValue, setValue] as const;
}